import { addShortcodeToEditor, getShortcodeWrapper } from "@/assets/utilities/tinymce/shortodeAddition";
import { getLiveChatV2Template } from "@/assets/utilities/tinymce/shortcodes/templates";
import { parseToHtml } from "@/utility/parseToHtml";
import { serializeDynamicComponents, visualizeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
import { convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
import { traverseTree } from "@/utility/traverseTree";
import { findInvalidTelHrefsDeep } from "@/utility/validateLiveChatTelHrefs";
import { showConfirmModal, showTelConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
import { flowErrorMessage } from "@/components/shortcodes/livechatV2/treeGraph";
import i18n from "@/i18n";

// `currentData` is null when nothing was edited — nothing new to validate.
const saveIfFlowConfirmed = (currentData, save) => {
  const flowError = currentData ? flowErrorMessage(currentData.messages) : null;

  if (!flowError) {
    save();
    return;
  }

  showConfirmModal({
    title: "Invalid chat flow",
    message: i18n.t("shortcodes.live_chat.invalid_flow_confirm"),
    valueLabel: "Problem:",
    value: flowError,
  }).then(confirmed => {
    if (confirmed) save();
  });
};

function serializeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => serializeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "_telHrefError") return;
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = serializeContent(obj[key]);
      }
    });

    return newObj;
  }
}

function visualizeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => visualizeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = visualizeContent(obj[key]);
      }
    });

    return newObj;
  }
}

const editLiveChatV2Config = (editor, shortcodeWrapper) => {
  let currentData = null;

  const liveChatWindow = editor.windowManager.openUrl({
    title: "Live Chat V2",
    url: "/live-chat-v2-dialog",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance, message) {
      const save = () => {
        editor.windowManager.alert("Success!");
        traverseTree(currentData.messages, message => {
          if (message.content) {
            message.content = convertAssetsPathToRelativeLinks(message.content)?.body?.innerHTML;
          }

          if (message.text) {
            message.text = convertAssetsPathToRelativeLinks(message.text)?.body?.innerHTML;
          }

          if (message.value) {
            message.value = convertAssetsPathToRelativeLinks(message.value)?.body?.innerHTML;
          }
        });
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapper.setAttribute("data-live-chat-data", encodedData);
        // We have to call change function here to updateMainContent in form component
        editor.fire("change");
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) saveIfFlowConfirmed(currentData, save);
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      saveIfFlowConfirmed(currentData, save);
    },
    onMessage: (instance, message) => {
      if (message.mceAction === "requestData") {
        sendData();
      }

      if (message.mceAction === "updateLiveChatData") {
        currentData = message.data;
      }
    },
    width: window.innerWidth - 20,
    height: window.innerHeight - 20,
  });

  const sendData = () => {
    const liveChatShortcodeData = shortcodeWrapper.getAttribute("data-live-chat-data");
    const parsedData = JSON.parse(decodeURIComponent(escape(window.atob(liveChatShortcodeData))));
    const content = visualizeContent(parsedData);

    liveChatWindow.sendMessage({
      type: "sendData",
      data: content,
    });
  };
};

const liveChatV2Config = (tinymce, uuid, shortcodeOnClick) => {
  let currentData = null;

  tinymce.activeEditor.windowManager.openUrl({
    title: "Live Chat V2",
    url: "/live-chat-v2-dialog?data=null",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance) {
      const save = () => {
        const shortcodeWrapper = getShortcodeWrapper(
          uuid,
          shortcodeOnClick,
          getLiveChatV2Template(),
          "live-chat-v2-shortcode",
        );

        traverseTree(currentData.messages, message => {
          if (message.content) {
            message.content = convertAssetsPathToRelativeLinks(message.content)?.body?.innerHTML;
          }

          if (message.text) {
            message.text = convertAssetsPathToRelativeLinks(message.text)?.body?.innerHTML;
          }

          if (message.value) {
            message.value = convertAssetsPathToRelativeLinks(message.value)?.body?.innerHTML;
          }
        });

        const shortcodeWrapperEl = parseToHtml(shortcodeWrapper).querySelector(".shortcode");
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapperEl.setAttribute("data-live-chat-data", encodedData);

        addShortcodeToEditor(shortcodeWrapperEl.outerHTML, uuid);
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) saveIfFlowConfirmed(currentData, save);
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      saveIfFlowConfirmed(currentData, save);
    },
    onMessage: (instance, message) => {
      currentData = message.data;
    },
    width: window.innerWidth - 20,
    height: window.innerHeight - 20,
  });
};

export { liveChatV2Config, editLiveChatV2Config };
